// @ts-nocheck
import { useState } from 'react';
import { Check, Pencil, Plus, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import CollapsibleSection from './CollapsibleSection';
import HeadingInfo from './HeadingInfo';
import { AI_AGENT_FIELD_LIMITS } from '@/utilities/constant';

const {
  FOLLOWUP_MAX_ITEMS: MAX_FOLLOW_UPS,
  FOLLOWUP_MESSAGE_MAX,
  FOLLOWUP_TIME_MIN,
  FOLLOWUP_TIME_MAX,
} = AI_AGENT_FIELD_LIMITS;

const INFO = {
  timeInterval: 'Seconds of caller silence before the agent says this follow-up message.',
  message: 'What the agent says after that silence. Example: “Are you still there?”',
};

const errClass = (hasErr: boolean) =>
  hasErr ? 'border-destructive focus-visible:ring-destructive' : '';


const getStepErrors = (step: any, index: number, steps: any[], showRequired: boolean) => {
  const e: any = {};
  const raw = String(step.waitTime ?? '').trim();

  if (raw === '') {
    if (showRequired) e.waitTime = 'Wait time is required';
  } else {
    const n = Number(raw);
    if (Number.isNaN(n) || n < FOLLOWUP_TIME_MIN || n > FOLLOWUP_TIME_MAX) {
      e.waitTime = `Wait time must be between ${FOLLOWUP_TIME_MIN} and ${FOLLOWUP_TIME_MAX} seconds`;
    } else {
      const prev = steps[index - 1];
      const prevN = prev ? Number(prev.waitTime) : NaN;
      if (prev && !Number.isNaN(prevN) && n <= prevN) {
        e.waitTime = `Must be longer than the previous follow-up (${prevN}s)`;
      }
    }
  }

  if (!step.message?.trim()) {
    if (showRequired) e.message = 'Follow-up message is required';
  } else if (step.message.length > FOLLOWUP_MESSAGE_MAX) {
    e.message = `Message can be max ${FOLLOWUP_MESSAGE_MAX} characters`;
  }
  return e;
};

export default function FollowUpRulesSection({ value, onChange }: any) {
  const steps = value?.steps || [];

  const [openIndex, setOpenIndex] = useState<number | null>(() => {
    const idx = steps.findIndex((s: any) => !s.message?.trim());
    return idx === -1 ? null : idx;
  });

  const [showRequired, setShowRequired] = useState(false);

  const emit = (nextSteps: any[]) =>
    onChange({ ...value, count: nextSteps.length, steps: nextSteps });

  const openStep = (index: number | null) => {
    setShowRequired(false);
    setOpenIndex(index);
  };

  const addStep = () => {
    if (steps.length >= MAX_FOLLOW_UPS) {
      toast.error(`You can add up to ${MAX_FOLLOW_UPS} follow-ups`);
      return;
    }
    const current = openIndex !== null ? steps[openIndex] : null;
    if (current && !current.message?.trim()) {
      setShowRequired(true);
      toast.error('Fill the current follow-up and click the tick first');
      return;
    }

    const last = steps[steps.length - 1];
    const lastN = last ? Number(last.waitTime) : NaN;
    const suggested = Number.isNaN(lastN)
      ? 10
      : Math.min(lastN + 5, FOLLOWUP_TIME_MAX);
    emit([...steps, { waitTime: String(suggested), message: '' }]);
    openStep(steps.length);
  };

  const updateStep = (index: number, patch: any) => {
    emit(steps.map((s: any, i: number) => (i === index ? { ...s, ...patch } : s)));
  };

  const removeStep = (index: number) => {
    emit(steps.filter((_: any, i: number) => i !== index));
    setShowRequired(false);
    setOpenIndex((prev) => {
      if (prev === null) return null;
      if (prev === index) return null;
      return prev > index ? prev - 1 : prev;
    });
  };

  const handleWaitTimeChange = (index: number, raw: string) => {
    if (raw !== '' && Number(raw) > FOLLOWUP_TIME_MAX) {
      toast.error(`Wait time can be max ${FOLLOWUP_TIME_MAX} seconds`, { id: 'followup-time-max' });
    }
    updateStep(index, { waitTime: raw });
  };

  const handleMessageChange = (index: number, raw: string) => {
    let text = raw;
    if (text.length > FOLLOWUP_MESSAGE_MAX) {
      toast.error(`Follow-up message can be max ${FOLLOWUP_MESSAGE_MAX} characters`, {
        id: 'followup-msg-max',
      });
      text = text.slice(0, FOLLOWUP_MESSAGE_MAX);
    }
    updateStep(index, { message: text });
  };

  // Tick: validate, then move the follow-up into the listing.
  const commitStep = (step: any, index: number) => {
    const errs = getStepErrors(step, index, steps, true);
    const first = errs.waitTime || errs.message;
    if (first) {
      setShowRequired(true);
      toast.error(first);
      return;
    }
    openStep(null);
  };

  return (
    <CollapsibleSection title="Follow-up Rules">
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm font-semibold text-foreground">
              Follow-up Rule
              <span className="ml-1.5 text-xs font-normal text-muted-foreground">
                ({steps.length}/{MAX_FOLLOW_UPS})
              </span>
            </p>
            <p className="mt-1 text-xs text-muted-foreground">
              Up to {MAX_FOLLOW_UPS} follow-ups. Wait time {FOLLOWUP_TIME_MIN}–{FOLLOWUP_TIME_MAX} seconds;
              each must be longer than the previous one.
            </p>
          </div>
          <Button
            size="sm"
            variant="outline"
            onClick={addStep}
            disabled={steps.length >= MAX_FOLLOW_UPS}
          >
            <Plus className="h-4 w-4" />
            Add
          </Button>
        </div>

        {steps.length === 0 && (
          <p className="py-4 text-center text-sm text-muted-foreground">No follow-ups added yet.</p>
        )}

        {steps.map((step: any, index: number) => {
          const errs = getStepErrors(step, index, steps, showRequired && index === openIndex);

          if (index === openIndex) {
            const msgLen = (step.message || '').length;
            const msgAtLimit = msgLen >= FOLLOWUP_MESSAGE_MAX;
            return (
              <div key={index} className="space-y-3 rounded-md border border-border p-3">
                <div className="flex items-center justify-between">
                  <p className="text-xs font-semibold text-foreground">Follow-up {index + 1}</p>
                  <span className="flex items-center gap-1">
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-7 w-7 text-emerald-600 hover:text-emerald-700"
                      onClick={() => commitStep(step, index)}
                      title="Done"
                      aria-label="Done"
                    >
                      <Check className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-7 w-7 text-destructive hover:text-destructive"
                      onClick={() => removeStep(index)}
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  </span>
                </div>

                <div className="grid grid-cols-1 gap-3 sm:grid-cols-[160px_1fr]">
                  <div className="space-y-1.5">
                    <Label className="flex items-center gap-1.5">
                      Wait Time (Seconds)
                      <HeadingInfo text={INFO.timeInterval} />
                    </Label>
                    <Input
                      type="number"
                      min={FOLLOWUP_TIME_MIN}
                      max={FOLLOWUP_TIME_MAX}
                      value={step.waitTime}
                      onChange={(e) => handleWaitTimeChange(index, e.target.value)}
                      className={errClass(!!errs.waitTime)}
                    />
                    {errs.waitTime && <p className="text-xs text-destructive">{errs.waitTime}</p>}
                  </div>
                  <div className="space-y-1.5">
                    <div className="flex items-center gap-1.5">
                      <Label>Message</Label>
                      <HeadingInfo text={INFO.message} />
                    </div>
                    <Textarea
                      rows={2}
                      placeholder="Hi, just checking if your issue was resolved?"
                      value={step.message}
                      onChange={(e) => handleMessageChange(index, e.target.value)}
                      className={errClass(!!errs.message || msgAtLimit)}
                      autoFocus
                    />
                    <div className="flex items-start justify-between gap-2">
                      <p className="text-xs text-destructive">
                        {errs.message || (msgAtLimit ? `Max ${FOLLOWUP_MESSAGE_MAX} characters reached` : '')}
                      </p>
                      <span className="shrink-0 text-xs text-muted-foreground">
                        {msgLen}/{FOLLOWUP_MESSAGE_MAX}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            );
          }

          return (
            <div
              key={index}
              className={`rounded-md border px-3 py-2 ${errs.waitTime ? 'border-destructive' : ''}`}
            >
              <div className="flex items-center justify-between">
                <div className="min-w-0">
                  <p className="truncate text-sm font-medium text-foreground">
                    Follow-up {index + 1}
                    <span className="ml-1.5 text-xs font-normal text-muted-foreground">
                      (after {step.waitTime || 0}s)
                    </span>
                  </p>
                  {step.message && (
                    <p className="truncate text-xs text-muted-foreground">{step.message}</p>
                  )}
                </div>
                <span className="flex shrink-0 items-center gap-1">
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8 text-primary hover:text-primary"
                    onClick={() => openStep(index)}
                  >
                    <Pencil className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8 text-destructive hover:text-destructive"
                    onClick={() => removeStep(index)}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </span>
              </div>
          
              {errs.waitTime && <p className="mt-1 text-xs text-destructive">{errs.waitTime}</p>}
            </div>
          );
        })}
      </div>
    </CollapsibleSection>
  );
}